import { graphql } from "gatsby"

import { APICommunityStep, Diet } from "./CommunityRecipe"
import {
  AccessMode,
  APIPartialRecipe,
  PartialRecipe,
  PartialRecipeWithVideo,
} from "./PartialRecipe"
import {
  ApiPartialPublicUser,
  apiPartialPublicUserToPartialPublicUser,
  PartialPublicUser,
} from "./PublicUser"
import { VerticalId } from "./Vertical"
import { Book } from "~/models/Book"

export type RecipeAlgoliaHit = {
  objectID: string
  id: string
  title: string
  subtitle: string
  vertical: string
  language: string
  categories_tree: {
    lvl0: string
    lvl1?: string
    lvl2?: string
    lvl3?: string
  }
  author: PartialPublicUser
  absolute_url: string
  web_cover_thumbnail: string
  web_cover: string
  web_cover_hd: string
  created_time: string
  published_date: string
  ratings_average: number | null
  ratings_count: number
  categories: string[]
  [key: string]: any
}

export type EditorialModule = {
  type: string
  title: string
  text: string
  position: number
  image: string
  imageThumbnail: string
}

export type RecipeType =
  | "default"
  | "kheops"
  | "community"
  | "classic"
  | "creator"

export type RecipeDifficulty =
  | "difficulty_0"
  | "difficulty_1"
  | "difficulty_2"
  | "difficulty_3"

export type ApiEditorialModule = {
  type: string
  title: string
  text: string
  position: number
  image: string
  image_thumbnail: string
}

export interface Recipe extends PartialRecipeWithVideo {
  absoluteUrl: string
  author: PartialPublicUser | null
  books: Book[]
  categories: string[]
  chefclubVideoId: string
  createdTime: string
  description: string
  ratingsAverage: number | null
  ratingsCount: number
  dominantColor: string
  durationMinutes: number
  difficulty: RecipeDifficulty
  preparationMinutes: number | null
  cookingMinutes: number | null
  restingMinutes: number | null
  ingredients: string[]
  ingredients_v2: Ingredient[]
  kitchenware: string[]
  kitchenware_v2: Kitchenware[]
  lastModified: number
  marketingDescription: string
  mobileShareUrl: string
  numberOfPeople: number
  // packs: string[]
  pinterestPicture: string
  pinterestPictureThumbnail: string
  premiumVideoUrl: string
  primaryCategory: {
    description: string | null
    title: string | null
    name: string | null
    uuid: string | null
  }
  primaryTags: string[]
  resultPicture: string
  resultPictureThumbnail: string
  shareUrl: string
  steps: string[]
  stepsDetails?: APICommunityStep[]
  suitableForDiets?: Diet[]
  sticker: string
  stickerPosition: string
  subtitle: string
  tags: string[]
  videoPreviewThumbnail: string
  videoUrl: string
  videoWidth: number
  videoHeight: number
  webCoverHd: string
  exploreRecipes: string[]
  trick: string | null
  postsCount: number
  viewsCount: number
  editorialModules: EditorialModule[]
  recipeType: RecipeType
  sameCategoryRecipes: PartialRecipe[]
  otherCategoryRecipes: PartialRecipe[]
}

export const query = graphql`
  fragment Recipe on Recipe {
    ...PartialRecipeWithVideo

    absoluteUrl: absolute_url
    author {
      ...PartialPublicUser
    }
    books
    categories
    chefclubVideoId: chefclub_video_id
    createdTime: created_time
    description
    ratingsCount: ratings_count
    ratingsAverage: ratings_average
    dominantColors: dominant_color
    durationMinutes: duration_minutes
    difficulty
    preparationMinutes: preparation_minutes
    restingMinutes: resting_minutes
    cookingMinutes: cooking_minutes
    ingredients
    ingredients_v2 {
      ingredient {
        name
        slug
        is_validated
        plural_name
      }
      unit {
        name
        plural_name
      }
      quantity
      result
    }
    kitchenware
    kitchenware_v2 {
      kitchenware {
        name
        url
      }
      quantity
    }
    lastModified: last_modified
    marketingDescription: marketing_description
    mobileShareUrl: mobile_share_url
    numberOfPeople: number_of_people
    pinterestPicture: pinterest_picture
    pinterestPictureThumbnail: pinterest_picture_thumbnail
    premiumVideoUrl: premium_video_url
    primaryCategory: primary_category {
      description
      title
      name
      uuid
    }
    primaryTags: primary_tags
    resultPicture: result_picture
    resultPictureThumbnail: result_picture_thumbnail
    shareUrl: share_url
    steps
    stepsDetails: steps_details {
      uuid
      description
      image
      image_hd
      image_thumbnail
      title
      order
      ingredients {
        uuid
        ingredient {
          uuid
          slug
          name
          plural_name
          is_validated
        }
        unit {
          uuid
          name
          plural_name
        }
        quantity
        result
      }
    }
    sticker
    stickerPosition: sticker_position
    subtitle
    tags
    videoPreviewThumbnail: video_preview_thumbnail
    videoUrl: video_url
    videoWidth: video_width
    videoHeight: video_height
    webCover: web_cover
    webCoverHd: web_cover_hd
    webCoverThumbnail: web_cover_thumbnail
    exploreRecipes
    trick
    viewsCount: views_count
    postsCount: posts_count
    editorialModules: editorial_modules {
      type
      title
      text
      position
      image
      imageThumbnail: image_thumbnail
    }
    recipeType: recipe_type
    sameCategoryRecipes: same_category_recipes {
      id
      slug
      title
      vertical
      webCover: web_cover
      webCoverThumbnail: web_cover_thumbnail
      language
      accessMode: access_mode
    }
    otherCategoryRecipes: other_category_recipes {
      id
      slug
      title
      vertical
      webCover: web_cover
      webCoverThumbnail: web_cover_thumbnail
      language
      accessMode: access_mode
    }
  }
`

export interface APIRecipe extends APIPartialRecipe {
  absolute_url: string
  author: ApiPartialPublicUser | null
  books: Book[]
  categories: string[]
  chefclub_video_id: string
  created_time: string
  description: string
  ratings_average: number | null
  ratings_count: number
  dominant_color: string
  duration_minutes: number
  difficulty: RecipeDifficulty
  preparation_minutes: number | null
  cooking_minutes: number | null
  resting_minutes: number | null
  hosted_video_url: string
  ingredients: string[]
  ingredients_v2: Ingredient[]
  kitchenware: string[]
  kitchenware_v2: Kitchenware[]
  last_modified: number
  marketing_description: string
  mobile_share_url: string
  number_of_people: number
  // packs: string[]
  pinterest_picture: string
  pinterest_picture_thumbnail: string
  premium_video_url: string
  primary_category: {
    description: string | null
    title: string | null
    name: string | null
    uuid: string | null
  }
  primary_tags: string[]
  result_picture: string
  result_picture_thumbnail: string
  share_url: string
  steps: string[]
  steps_details?: APICommunityStep[]
  sticker: string
  sticker_position: string
  subtitle: string
  suitable_for_diets: Diet[]
  tags: string[]
  title: string
  video_preview: string
  video_preview_thumbnail: string
  video_url: string
  video_width: number
  video_height: number
  web_cover_hd: string
  exploreRecipes: string[]
  trick: string | null
  posts_count: number
  views_count: number
  editorial_modules: ApiEditorialModule[]
  recipe_type: RecipeType
}

export const apiRecipeToRecipe = ({
  id,
  uuid,
  url_slug,
  title,
  vertical,
  web_cover,
  web_cover_thumbnail,
  language,
  access_mode,
  absolute_url = "",
  books = [],
  categories = [],
  chefclub_video_id = "",
  created_time = "",
  description = "",
  ratings_average = null,
  ratings_count = 0,
  dominant_color = "",
  duration_minutes = 0,
  difficulty = "difficulty_0",
  preparation_minutes = null,
  cooking_minutes = null,
  resting_minutes = null,
  hosted_video_url = "",
  ingredients = [],
  ingredients_v2 = [],
  kitchenware = [],
  kitchenware_v2 = [],
  last_modified,
  marketing_description = "",
  mobile_share_url = "",
  number_of_people,
  // packs: string[]
  pinterest_picture = "",
  pinterest_picture_thumbnail = "",
  premium_video_url = "",
  primary_category,
  primary_tags = [],
  result_picture = "",
  result_picture_thumbnail = "",
  share_url = "",
  steps = [],
  steps_details = undefined,
  sticker = "",
  sticker_position = "",
  subtitle = "",
  suitable_for_diets = [],
  tags = [],
  video_preview = "",
  video_preview_thumbnail = "",
  video_url = "",
  video_width = 1080,
  video_height = 1080,
  web_cover_hd = "",
  exploreRecipes = [],
  trick = null,
  posts_count = 0,
  views_count = 0,
  author,
  editorial_modules = [],
  recipe_type,
}: APIRecipe): Recipe => ({
  id: uuid ?? id,
  slug: url_slug,
  title,
  vertical: vertical as VerticalId,
  webCover: web_cover,
  webCoverThumbnail: web_cover_thumbnail,
  language,
  accessMode: access_mode as AccessMode,
  postsCount: posts_count,
  absoluteUrl: absolute_url,
  books,
  categories,
  chefclubVideoId: chefclub_video_id,
  createdTime: created_time,
  description,
  ratingsAverage: ratings_average,
  ratingsCount: ratings_count,
  dominantColor: dominant_color,
  durationMinutes: duration_minutes,
  difficulty,
  preparationMinutes: preparation_minutes,
  cookingMinutes: cooking_minutes,
  restingMinutes: resting_minutes,
  ingredients,
  ingredients_v2,
  kitchenware,
  kitchenware_v2,
  lastModified: last_modified,
  marketingDescription: marketing_description,
  mobileShareUrl: mobile_share_url,
  numberOfPeople: number_of_people,
  // packs,
  pinterestPicture: pinterest_picture,
  pinterestPictureThumbnail: pinterest_picture_thumbnail,
  premiumVideoUrl: premium_video_url,
  primaryCategory: primary_category ?? {
    description: null,
    title: null,
    name: null,
    uuid: null,
  },
  primaryTags: primary_tags,
  resultPicture: result_picture,
  resultPictureThumbnail: result_picture_thumbnail,
  shareUrl: share_url,
  steps,
  stepsDetails: steps_details,
  sticker,
  stickerPosition: sticker_position,
  subtitle,
  suitableForDiets: suitable_for_diets,
  tags,
  videoPreviewThumbnail: video_preview_thumbnail,
  videoUrl: video_url,
  videoWidth: video_width,
  videoHeight: video_height,
  webCoverHd: web_cover_hd,
  videoPreview: video_preview,
  hostedVideoUrl: hosted_video_url,
  exploreRecipes,
  trick,
  viewsCount: views_count,
  author: author ? apiPartialPublicUserToPartialPublicUser(author) : null,
  editorialModules: apiEditorialModulesToEditorialModules(editorial_modules),
  recipeType: recipe_type,
  sameCategoryRecipes: [],
  otherCategoryRecipes: [],
})

export type Ingredient = {
  uuid: string
  ingredient: {
    uuid: string
    name: string
    slug: string
    plural_name?: string | null
    is_validated?: boolean
  }
  unit: {
    uuid: string
    name: string
    plural_name?: string | null
  }
  quantity: number | null
  result: string
}

export type Kitchenware = {
  uuid: string
  kitchenware: {
    uuid: string
    name: string
    url: string
    plural_name?: string | null
  }
  quantity: number
}

export const apiEditorialModulesToEditorialModules = (
  props: ApiEditorialModule[]
): EditorialModule[] => {
  return props.map(prop => {
    return {
      ...prop,
      imageThumbnail: prop.image_thumbnail,
    }
  })
}
